import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getSchools, deleteSchool, createSchool, updateSchool } from "../../../utils/api";

const columns = [
  { key: "name", label: "Name" },
  { key: "location", label: "Location" },
  {
    key: "contact",
    label: "Contact",
    render: (value) => {
      if (!value) return "-";
      const parts = [value.phone, value.email].filter(Boolean);
      return parts.length ? parts.join(" • ") : "-";
    }
  },
  {
    key: "adres",
    label: "Address",
    render: (value) => {
      if (!value) return "-";
      return [value.street, value.city, value.region, value.country].filter(Boolean).join(", ") || "-";
    }
  },
  { key: "establishedYear", label: "Established Year" },
];

const filters = [
  {
    key: "location",
    label: "Filter by Location",
    type: "search"
  },
];

const initialFormData = {
  name: "",
  description: "",
  location: "",
  contact: {
    phone: "",
    email: ""
  },
  website: "",
  establishedYear: "",
  adres: {
    street: "",
    city: "",
    region: "",
    country: ""
  },
  imageUrl: ""
};

const buildSchoolPayload = (formData) => ({
  name: formData.name.trim(),
  description: formData.description.trim(),
  location: formData.location.trim(),
  contact: {
    phone: formData.contact.phone.trim(),
    email: formData.contact.email.trim(),
  },
  website: formData.website.trim(),
  adres: {
    street: formData.adres.street.trim(),
    city: formData.adres.city.trim(),
    region: formData.adres.region.trim(),
    country: formData.adres.country.trim(),
  },
  establishedYear: formData.establishedYear ? Number(formData.establishedYear) : undefined,
  imageUrl: formData.imageUrl,
});

export default function Schools() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name || "",
      description: item.description || "",
      location: item.location || "",
      contact: {
        phone: item.contact?.phone || "",
        email: item.contact?.email || "",
      },
      website: item.website || "",
      establishedYear: item.establishedYear || "",
      adres: item.adres || { street: "", city: "", region: "", country: "" },
      imageUrl: item.imageUrl || ""
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (item) => {
    handleEdit(item);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("contact.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        contact: { ...prev.contact, [field]: value }
      }));
    } else if (name.startsWith("adres.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({ 
        ...prev, 
        adres: { ...prev.adres, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, imageUrl: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = buildSchoolPayload(formData);

    try {
      if (isEditMode && selectedItem) {
        await updateSchool(selectedItem._id, payload);
        alert("Updated successfully!");
      } else {
        await createSchool(payload);
        alert("Created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Schools"
          fetchData={getSchools}
          deleteData={deleteSchool}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search schools..."
        />
      </main>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit School" : "Add New School"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>School Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                placeholder="Enter school name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="3"
                placeholder="Enter description"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  name="contact.phone"
                  value={formData.contact.phone}
                  onChange={handleFormChange}
                  placeholder="Phone number"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="contact.email"
                  value={formData.contact.email}
                  onChange={handleFormChange}
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleFormChange}
                placeholder="Enter location"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Address</h3>
            <div className="form-group">
              <label>Street</label>
              <input
                type="text"
                name="adres.street"
                value={formData.adres.street}
                onChange={handleFormChange}
                placeholder="Street address"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="adres.city"
                  value={formData.adres.city}
                  onChange={handleFormChange}
                  placeholder="City"
                />
              </div>
              <div className="form-group">
                <label>Region</label>
                <input
                  type="text"
                  name="adres.region"
                  value={formData.adres.region}
                  onChange={handleFormChange}
                  placeholder="Region"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="adres.country"
                  value={formData.adres.country}
                  onChange={handleFormChange}
                  placeholder="Country"
                />
              </div>
              <div className="form-group">
                <label>Established Year</label>
                <input
                  type="text"
                  name="establishedYear"
                  value={formData.establishedYear}
                  onChange={handleFormChange}
                  placeholder="e.g., 1995"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Additional Info</h3>
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleFormChange}
                placeholder="https://schoolwebsite.com"
              />
            </div>
            <div className="form-group">
              <label>Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update" : "Create")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
